<template>
  <div class="container mx-auto px-4 py-4 text-gray-900 dark:text-gray-100">
    <PageHeading
      class="mb-6"
      :title="heading"
      description="Name, contact details, password, and security settings"
      :crumbs="crumbs"
    >
      <template v-if="user" #title-extra>
        <span :class="[badgeClass, roleBadgeClass(user.role)]">{{ roleLabel(user.role) }}</span>
        <span :class="[badgeClass, statusBadgeClass(user.isActive !== false)]">
          {{ user.isActive === false ? "Inactive" : "Active" }}
        </span>
        <span :class="[badgeClass, totpBadgeClass]">
          {{ user.totpEnabled ? "2FA on" : "2FA off" }}
        </span>
      </template>
      <template v-if="user" #actions>
        <p v-if="saveError" class="text-sm text-red-600">{{ saveError }}</p>
        <p v-else-if="saveMessage" class="text-sm text-green-600">{{ saveMessage }}</p>
        <PageSecondaryButton class="min-w-32" @click="cancel">Cancel</PageSecondaryButton>
        <PagePrimaryButton
          class="min-w-32"
          type="submit"
          form="user-profile-form"
          :disabled="saving"
        >
          <i class="pi pi-save shrink-0 text-base" aria-hidden="true" />
          {{ saving ? "Saving..." : "Save" }}
        </PagePrimaryButton>
      </template>
    </PageHeading>

    <p v-if="loading" class="text-sm text-gray-500">Loading profile...</p>
    <p v-else-if="loadError" class="text-sm text-red-600">{{ loadError }}</p>

    <div v-else class="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
      <form id="user-profile-form" class="space-y-6" @submit.prevent="saveProfile">
        <section :class="cardClass">
          <div :class="cardHeaderClass">
            <h2 :class="cardTitleClass">Profile</h2>
            <p :class="cardHintClass">The account handle and contact details.</p>
          </div>
          <div class="grid gap-4 p-6 sm:grid-cols-2">
            <div>
              <label :class="labelClass" for="profile-username">Username</label>
              <input id="profile-username" v-model="form.username" required :class="inputClass" />
            </div>
            <div>
              <label :class="labelClass" for="profile-email">Email</label>
              <input
                id="profile-email"
                v-model="form.email"
                type="email"
                required
                :class="inputClass"
              />
            </div>
            <div>
              <label :class="labelClass" for="profile-first-name">Name</label>
              <input id="profile-first-name" v-model="form.firstName" :class="inputClass" />
            </div>
            <div>
              <label :class="labelClass" for="profile-last-name">Family name</label>
              <input id="profile-last-name" v-model="form.lastName" :class="inputClass" />
            </div>
            <div>
              <label :class="labelClass" for="profile-phone">Phone</label>
              <input id="profile-phone" v-model="form.phone" type="tel" :class="inputClass" />
            </div>
          </div>
        </section>

        <section v-if="!isSelf" :class="cardClass">
          <div :class="cardHeaderClass">
            <h2 :class="cardTitleClass">Access</h2>
            <p :class="cardHintClass">What this account may do, and whether it can sign in.</p>
          </div>
          <div class="grid gap-4 p-6 sm:grid-cols-2">
            <div>
              <label :class="labelClass" for="profile-role">Role</label>
              <select id="profile-role" v-model="form.role" :class="inputClass">
                <option value="user">User</option>
                <option value="helper">Helper</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <div>
              <span :class="labelClass" id="profile-active-label">Status</span>
              <div class="flex h-11 items-center gap-3">
                <button
                  id="profile-active"
                  type="button"
                  role="switch"
                  :aria-checked="form.isActive"
                  aria-labelledby="profile-active-label"
                  :class="[
                    'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900',
                    form.isActive ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-500'
                  ]"
                  @click="form.isActive = !form.isActive"
                >
                  <span
                    :class="[
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/10 transition duration-200 ease-in-out',
                      form.isActive ? 'translate-x-5' : 'translate-x-0'
                    ]"
                  />
                </button>
                <span class="text-sm text-gray-700 dark:text-gray-200">
                  {{ form.isActive ? "Active" : "Inactive" }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section :class="cardClass">
          <div :class="cardHeaderClass">
            <h2 :class="cardTitleClass">Password</h2>
            <p :class="cardHintClass">
              {{
                isSelf
                  ? "Leave these blank to keep your current password."
                  : "Set a new password for this account, or leave it blank to keep the current one."
              }}
            </p>
          </div>
          <div class="space-y-4 p-6">
            <div v-if="isSelf" class="grid gap-4 sm:grid-cols-2">
              <div>
                <label :class="labelClass" for="profile-current-password">Current password</label>
                <input
                  id="profile-current-password"
                  v-model="form.currentPassword"
                  type="password"
                  autocomplete="current-password"
                  placeholder="Required for a new password"
                  :class="inputClass"
                />
              </div>
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <div>
                <label :class="labelClass" for="profile-new-password">New password</label>
                <input
                  id="profile-new-password"
                  v-model="form.newPassword"
                  type="password"
                  autocomplete="new-password"
                  :class="inputClass"
                />
              </div>
              <div v-if="isSelf">
                <label :class="labelClass" for="profile-confirm-password">
                  Confirm new password
                </label>
                <input
                  id="profile-confirm-password"
                  v-model="form.confirmPassword"
                  type="password"
                  autocomplete="new-password"
                  :class="inputClass"
                />
              </div>
            </div>
          </div>
        </section>
      </form>

      <aside class="space-y-6">
        <section :class="cardClass">
          <div :class="cardHeaderClass">
            <h2 :class="cardTitleClass">Security settings</h2>
            <p :class="cardHintClass">
              Authenticator app codes are required at sign-in when this is on.
            </p>
          </div>
          <div class="space-y-4 p-6">
            <p class="text-sm text-gray-700 dark:text-gray-200">
              Two-factor authentication is
              <strong>{{ user?.totpEnabled ? "on" : "off" }}</strong
              >.
            </p>

            <template v-if="isSelf && !user?.totpEnabled">
              <PagePrimaryButton v-if="!setup" :disabled="securityLoading" @click="startSetup">
                Turn on authenticator app
              </PagePrimaryButton>
              <div v-else class="space-y-4">
                <div
                  class="flex flex-col items-center gap-3 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/60"
                >
                  <img
                    :src="setup.qr"
                    alt="Authenticator QR code"
                    class="h-40 w-40 rounded-md bg-white p-2"
                  />
                  <p class="text-center text-xs text-gray-500 dark:text-gray-400">
                    Scan this with your authenticator app, or enter the key below by hand.
                  </p>
                  <code
                    class="w-full break-all rounded border border-gray-200 bg-white px-2 py-1 text-center text-xs text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
                  >
                    {{ setup.secret }}
                  </code>
                </div>
                <div>
                  <label :class="labelClass" for="totp-confirm-code">Authentication code</label>
                  <input
                    id="totp-confirm-code"
                    v-model="confirmCode"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    :class="inputClass"
                  />
                </div>
                <PagePrimaryButton class="w-full" :disabled="securityLoading" @click="confirmSetup">
                  Confirm and turn on
                </PagePrimaryButton>
              </div>
            </template>

            <div
              v-if="recoveryCodes.length"
              class="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
            >
              <p class="font-medium">Save these recovery codes. They are shown once.</p>
              <ul class="mt-2 grid grid-cols-2 gap-1 font-mono text-[13px]">
                <li v-for="recoveryCode in recoveryCodes" :key="recoveryCode">
                  {{ recoveryCode }}
                </li>
              </ul>
            </div>

            <form v-if="isSelf && user?.totpEnabled" class="space-y-4" @submit.prevent="disableOwn">
              <div>
                <label :class="labelClass" for="totp-disable-password">Confirm your password</label>
                <input
                  id="totp-disable-password"
                  v-model="disableForm.password"
                  type="password"
                  autocomplete="current-password"
                  :class="inputClass"
                />
              </div>
              <div>
                <label :class="labelClass" for="totp-disable-code">Authentication code</label>
                <input
                  id="totp-disable-code"
                  v-model="disableForm.code"
                  autocomplete="one-time-code"
                  :class="inputClass"
                />
              </div>
              <button type="submit" :class="dangerButtonClass" :disabled="securityLoading">
                Turn off
              </button>
            </form>

            <div v-if="!isSelf && user?.totpEnabled" class="space-y-3">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Turn this off if they lost the authenticator device. They can set it up again from
                their own profile.
              </p>
              <button
                type="button"
                :class="dangerButtonClass"
                :disabled="securityLoading"
                @click="disableOther"
              >
                Turn off two-factor authentication
              </button>
            </div>

            <p v-if="securityError" class="text-sm text-red-600">{{ securityError }}</p>
          </div>
        </section>

        <section :class="cardClass">
          <div :class="cardHeaderClass">
            <h2 :class="cardTitleClass">Account</h2>
          </div>
          <dl class="divide-y divide-gray-200 px-6 text-sm dark:divide-gray-700">
            <div
              v-for="row in accountRows"
              :key="row.label"
              class="flex items-center justify-between gap-4 py-3"
            >
              <dt class="text-gray-500 dark:text-gray-400">{{ row.label }}</dt>
              <dd class="font-medium text-gray-900 dark:text-white">{{ row.value }}</dd>
            </div>
          </dl>
        </section>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import QRCode from "qrcode";
import { useAuthStore } from "../../stores/auth";
import api from "../../services/api";
import PageHeading from "../../components/common/PageHeading.vue";
import PagePrimaryButton from "../../components/common/PagePrimaryButton.vue";
import PageSecondaryButton from "../../components/common/PageSecondaryButton.vue";
import {
  badgeClass,
  formatRelative,
  roleBadgeClass,
  roleLabel,
  statusBadgeClass
} from "../../utils/userDisplay";

defineOptions({ name: "UserProfile" });

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const loading = ref(true);
const saving = ref(false);
const securityLoading = ref(false);
const loadError = ref("");
const saveError = ref("");
const saveMessage = ref("");
const securityError = ref("");
const user = ref(null);
const setup = ref(null);
const confirmCode = ref("");
const recoveryCodes = ref([]);
const disableForm = ref({ password: "", code: "" });
const form = ref(emptyForm());

const labelClass = "mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200";
const inputClass =
  "h-11 w-full rounded-md border border-gray-300 bg-transparent px-3 text-sm text-gray-900 shadow-sm outline-none focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:text-white";
const cardClass =
  "rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900";
const cardHeaderClass = "border-b border-gray-200 px-6 py-4 dark:border-gray-700";
const cardTitleClass = "text-base font-semibold text-gray-900 dark:text-white";
const cardHintClass = "mt-0.5 text-[13px] text-gray-500 dark:text-gray-400";
const dangerButtonClass =
  "inline-flex h-10 items-center justify-center rounded-lg border border-red-300 px-4 text-[13px] font-bold text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/40";

const isSelf = computed(() => String(route.params.id) === String(authStore.user?.id));

function cancel() {
  if (authStore.isAdmin) {
    router.push({ name: "Users" });
    return;
  }
  router.push(authStore.isHelper ? "/events/live" : "/");
}
const heading = computed(() => user.value?.username || "Profile");
const crumbs = computed(() => {
  const trail = [{ label: "User Management" }];
  if (authStore.isAdmin) trail.push({ label: "Users", to: "/users" });
  trail.push({ label: heading.value });
  return trail;
});
const totpBadgeClass = computed(() =>
  user.value?.totpEnabled
    ? "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-300"
    : "border-gray-300 bg-gray-100 text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
);
const accountRows = computed(() => [
  { label: "Last sign-in", value: formatRelative(user.value?.lastLogin) },
  { label: "Account created", value: formatRelative(user.value?.createdAt) },
  { label: "User ID", value: user.value?.id ?? "" }
]);

function emptyForm() {
  return {
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    role: "user",
    isActive: true
  };
}

function applyUser(loaded) {
  user.value = loaded;
  form.value = {
    ...emptyForm(),
    username: loaded.username || "",
    firstName: loaded.firstName || "",
    lastName: loaded.lastName || "",
    email: loaded.email || "",
    phone: loaded.phone || "",
    role: loaded.role || "user",
    isActive: loaded.isActive !== false
  };
}

async function load() {
  loading.value = true;
  loadError.value = "";
  setup.value = null;
  recoveryCodes.value = [];
  try {
    const url = isSelf.value ? "/api/auth/profile" : `/api/users/${route.params.id}`;
    const response = await api.get(url);
    applyUser(response.data.data.user);
  } catch (err) {
    loadError.value = err.response?.data?.message || "Failed to load profile";
  } finally {
    loading.value = false;
  }
}

async function saveProfile() {
  saveError.value = "";
  saveMessage.value = "";
  if (
    isSelf.value &&
    form.value.newPassword &&
    form.value.newPassword !== form.value.confirmPassword
  ) {
    saveError.value = "New passwords do not match";
    return;
  }
  saving.value = true;
  try {
    if (isSelf.value) {
      const payload = {
        username: form.value.username,
        email: form.value.email,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        phone: form.value.phone
      };
      if (form.value.newPassword) {
        payload.currentPassword = form.value.currentPassword;
        payload.newPassword = form.value.newPassword;
      }
      const result = await authStore.updateProfile(payload);
      if (!result?.success) {
        saveError.value = result?.message || "Failed to update profile";
        return;
      }
      applyUser(authStore.user);
    } else {
      const payload = {
        username: form.value.username,
        email: form.value.email,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        phone: form.value.phone,
        role: form.value.role,
        isActive: form.value.isActive
      };
      if (form.value.newPassword.trim()) payload.password = form.value.newPassword.trim();
      const response = await api.put(`/api/users/${route.params.id}`, payload);
      applyUser({ ...user.value, ...response.data.data.user });
    }
    form.value.currentPassword = "";
    form.value.newPassword = "";
    form.value.confirmPassword = "";
    saveMessage.value = "Profile saved";
  } catch (err) {
    saveError.value = err.response?.data?.message || "Failed to update profile";
  } finally {
    saving.value = false;
  }
}

async function startSetup() {
  securityError.value = "";
  securityLoading.value = true;
  try {
    const response = await api.post("/api/auth/totp/setup");
    const { otpauthUrl, secret } = response.data.data;
    setup.value = {
      secret,
      qr: await QRCode.toDataURL(otpauthUrl)
    };
  } catch (err) {
    securityError.value = err.response?.data?.message || "Failed to start setup";
  } finally {
    securityLoading.value = false;
  }
}

async function confirmSetup() {
  securityError.value = "";
  securityLoading.value = true;
  try {
    const response = await api.post("/api/auth/totp/confirm", { code: confirmCode.value });
    recoveryCodes.value = response.data.data.recoveryCodes || [];
    confirmCode.value = "";
    setup.value = null;
    user.value = { ...user.value, totpEnabled: true };
    if (authStore.user) authStore.user = { ...authStore.user, totpEnabled: true };
  } catch (err) {
    securityError.value = err.response?.data?.message || "Invalid authentication code";
  } finally {
    securityLoading.value = false;
  }
}

async function disableOwn() {
  securityError.value = "";
  securityLoading.value = true;
  try {
    await api.post("/api/auth/totp/disable", {
      password: disableForm.value.password,
      code: disableForm.value.code
    });
    disableForm.value = { password: "", code: "" };
    recoveryCodes.value = [];
    user.value = { ...user.value, totpEnabled: false };
    if (authStore.user) authStore.user = { ...authStore.user, totpEnabled: false };
  } catch (err) {
    securityError.value =
      err.response?.data?.message || "Failed to turn off two-factor authentication";
  } finally {
    securityLoading.value = false;
  }
}

async function disableOther() {
  securityError.value = "";
  securityLoading.value = true;
  try {
    await api.post(`/api/users/${route.params.id}/totp/disable`);
    user.value = { ...user.value, totpEnabled: false };
  } catch (err) {
    securityError.value =
      err.response?.data?.message || "Failed to turn off two-factor authentication";
  } finally {
    securityLoading.value = false;
  }
}

watch(() => route.params.id, load);
onMounted(load);
</script>
